import { STORE_CURRENCY } from "../constants/wordpress";
import { extractProductColors } from "./productColors";

export function stripHtml(html = "") {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function formatPrice(price, currency = STORE_CURRENCY) {
  if (price === null || price === undefined || price === "") return null;
  const amount = Number.parseFloat(price);
  if (Number.isNaN(amount)) return null;

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${price} ${currency}`;
  }
}

export function formatProductUnitLabel(unitWeight = "", unit = "") {
  const unitWeightValue = String(unitWeight || "").trim();
  const unitValue = String(unit || "").trim();

  if (unitWeightValue && unitValue) return `${unitWeightValue} ${unitValue}`;
  return unitWeightValue || unitValue || "";
}

export function getProductUnitDisplay(product) {
  if (!product) return "";
  if (product.unitLabel) return product.unitLabel;

  const unitWeight = String(product.unitWeight || "").trim();
  const unit = String(product.unit || "").trim();
  return formatProductUnitLabel(unitWeight, unit);
}

/** @deprecated Use getProductUnitDisplay */
export function getProductWeightDisplay(product) {
  return getProductUnitDisplay(product);
}

export function formatPriceWithUnit(price, unit, currency = STORE_CURRENCY) {
  const formattedPrice = formatPrice(price, currency);
  const unitValue = String(unit || "").trim();

  if (!formattedPrice) return null;
  if (!unitValue) return formattedPrice;

  return `${formattedPrice} / ${unitValue}`;
}

export function getDisplayCategories(categories = []) {
  return categories.filter(
    (category) => category.slug !== "uncategorized" && category.name,
  );
}

function getMetaValue(metaData = [], key) {
  const entry = metaData.find((item) => item.key === key);
  const value = entry?.value;
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function buildProductTranslations(raw) {
  const ar = {
    name: raw.name || "",
    shortDescription: stripHtml(raw.short_description),
    description: raw.description || "",
  };

  const nameEn = getMetaValue(raw.meta_data, "product_name_en");
  const shortDescriptionEn = getMetaValue(raw.meta_data, "short_description_en");
  const descriptionEn = getMetaValue(raw.meta_data, "description_en");

  const en = {
    name: nameEn || ar.name,
    shortDescription: shortDescriptionEn
      ? stripHtml(shortDescriptionEn)
      : ar.shortDescription,
    description: descriptionEn || ar.description,
  };

  return { ar, en };
}

export function getProductLanguage(language) {
  return language?.startsWith("ar") ? "ar" : "en";
}

function buildCategoryTranslations(raw) {
  const nameEn = String(raw.name || "").trim();
  const nameAr = String(raw.category_name_ar || raw.name || "").trim();

  return {
    ar: { name: nameAr || nameEn },
    en: { name: nameEn || nameAr },
  };
}

export function localizeCategory(category, language) {
  if (!category) return null;

  const lang = getProductLanguage(language);
  const localizedName = category.translations?.[lang]?.name || category.name;

  return {
    ...category,
    name: localizedName,
  };
}

export function localizeProduct(product, language, categoryById) {
  if (!product) return null;

  const lang = getProductLanguage(language);
  const localized = product.translations?.[lang];
  const categories = (product.categories || []).map((cat) => {
    const full = categoryById?.get(cat.id) || cat;
    return localizeCategory(full, language);
  });

  if (!localized) {
    return {
      ...product,
      categories,
    };
  }

  return {
    ...product,
    name: localized.name || product.name,
    shortDescription: localized.shortDescription ?? product.shortDescription,
    description: localized.description ?? product.description,
    categories,
  };
}

export function mapWooProduct(raw) {
  if (!raw) return null;

  const image = raw.images?.[0]?.src || null;
  const gallery = (raw.images || []).map((img) => img.src).filter(Boolean);
  const categories = (raw.categories || []).map((cat) => ({
    id: cat.id,
    name: cat.name,
    slug: cat.slug,
  }));

  const price = raw.price || raw.sale_price || raw.regular_price || "";
  const unit = getMetaValue(raw.meta_data, "unit");
  const unitWeight = getMetaValue(raw.meta_data, "unit_weight");
  const unitLabel = formatProductUnitLabel(unitWeight, unit);
  const inStock = raw.stock_status !== "outofstock";
  const translations = buildProductTranslations(raw);
  const colors = extractProductColors(raw.attributes);
  const colorAttribute = (raw.attributes || []).find(
    (attr) =>
      attr.slug === "pa_color" || String(attr.name || "").toLowerCase() === "color",
  );

  return {
    id: raw.id,
    slug: raw.slug,
    name: translations.ar.name,
    shortDescription: translations.ar.shortDescription,
    description: translations.ar.description,
    translations,
    price,
    unit,
    unitWeight,
    unitLabel,
    regularPrice: raw.regular_price,
    salePrice: raw.sale_price,
    onSale: Boolean(raw.on_sale),
    formattedPrice: formatPrice(price),
    formattedPriceWithUnit: formatPriceWithUnit(price, unit),
    formattedRegularPrice: formatPrice(raw.regular_price),
    formattedRegularPriceWithUnit: formatPriceWithUnit(raw.regular_price, unit),
    formattedSalePrice: formatPrice(raw.sale_price),
    formattedSalePriceWithUnit: formatPriceWithUnit(raw.sale_price, unit),
    currency: STORE_CURRENCY,
    image,
    gallery,
    categories,
    sku: raw.sku || "",
    permalink: raw.permalink,
    purchasable: raw.purchasable !== false,
    inStock,
    stockStatus: raw.stock_status || "instock",
    relatedIds: raw.related_ids || [],
    colors,
    colorAttributeName: colorAttribute?.name || "Color",
  };
}

export function mapWooCategory(raw) {
  if (!raw) return null;

  const translations = buildCategoryTranslations(raw);

  return {
    id: raw.id,
    name: translations.ar.name,
    slug: raw.slug,
    count: raw.count,
    translations,
  };
}
